import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { fetchVideoPlaybackSource } from '../../../services/video.service';
import { Routes } from '../../../types/constants';
import styles from './AdminVideoPlayerTools.module.scss';

const viewportMargin = 12;
const previewMaxWidth = 480;
const previewHeaderFallbackHeight = 64;
const actionMenuWidth = 196;
const actionMenuHeight = 92;
const actionMenuGap = 6;
const actionMenuMargin = 8;

export const clampPreviewPosition = (
    nextX: number,
    nextY: number,
    viewportWidth: number,
    viewportHeight: number,
    panelWidth: number,
    panelHeight: number
) => ({
    x: Math.min(Math.max(nextX, viewportMargin), Math.max(viewportMargin, viewportWidth - panelWidth - viewportMargin)),
    y: Math.min(Math.max(nextY, viewportMargin), Math.max(viewportMargin, viewportHeight - panelHeight - viewportMargin))
});

export interface AdminVideoPreviewTarget {
    title: string;
    shortcode: string;
}

interface AdminVideoActionsMenuProps extends AdminVideoPreviewTarget {
    canPreview?: boolean;
    onPreview: () => void;
}

export const AdminVideoActionsMenu = ({ title, shortcode, canPreview = true, onPreview }: AdminVideoActionsMenuProps) => {
    const menuId = useId();
    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const menuRef = useRef<HTMLDivElement | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [position, setPosition] = useState({ left: actionMenuMargin, top: actionMenuMargin });

    const closeMenu = useCallback((restoreFocus = false) => {
        setIsOpen(false);
        if (restoreFocus) {
            window.requestAnimationFrame(() => triggerRef.current?.focus());
        }
    }, []);

    const updatePosition = useCallback(() => {
        const trigger = triggerRef.current;
        if (!trigger) return;

        const bounds = trigger.getBoundingClientRect();
        const menuWidth = menuRef.current?.offsetWidth || actionMenuWidth;
        const menuHeight = menuRef.current?.offsetHeight || actionMenuHeight;
        const maximumLeft = Math.max(actionMenuMargin, window.innerWidth - menuWidth - actionMenuMargin);
        const left = Math.min(Math.max(bounds.right - menuWidth, actionMenuMargin), maximumLeft);
        const belowTop = bounds.bottom + actionMenuGap;
        const top = belowTop + menuHeight <= window.innerHeight - actionMenuMargin
            ? belowTop
            : Math.max(actionMenuMargin, bounds.top - menuHeight - actionMenuGap);

        setPosition((current) => current.left === left && current.top === top ? current : { left, top });
    }, []);

    useLayoutEffect(() => {
        if (!isOpen) return undefined;
        updatePosition();
        const frame = window.requestAnimationFrame(() => {
            updatePosition();
            menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
        });
        return () => window.cancelAnimationFrame(frame);
    }, [isOpen, updatePosition]);

    useEffect(() => {
        if (!isOpen) return undefined;

        const handlePointerDown = (event: PointerEvent) => {
            const target = event.target as Node;
            if (!triggerRef.current?.contains(target) && !menuRef.current?.contains(target)) {
                closeMenu();
            }
        };
        const handleFocus = (event: FocusEvent) => {
            const target = event.target as Node;
            if (!triggerRef.current?.contains(target) && !menuRef.current?.contains(target)) {
                closeMenu();
            }
        };
        const handleKeyDown = (event: globalThis.KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                closeMenu(true);
            }
        };

        document.addEventListener('pointerdown', handlePointerDown, true);
        document.addEventListener('focusin', handleFocus);
        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);
        return () => {
            document.removeEventListener('pointerdown', handlePointerDown, true);
            document.removeEventListener('focusin', handleFocus);
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [closeMenu, isOpen, updatePosition]);

    const handleMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
        const items = Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);
        if (items.length === 0) return;

        event.preventDefault();
        const currentIndex = items.indexOf(document.activeElement as HTMLElement);
        const nextIndex = event.key === 'Home'
            ? 0
            : event.key === 'End'
                ? items.length - 1
                : event.key === 'ArrowDown'
                    ? (currentIndex + 1 + items.length) % items.length
                    : (currentIndex - 1 + items.length) % items.length;
        items[nextIndex].focus();
    };

    const menu = isOpen && typeof document !== 'undefined'
        ? createPortal(
            <div
                ref={menuRef}
                id={menuId}
                role="menu"
                aria-label={`Video actions for ${title}`}
                className={styles.actionsPopover}
                style={{ left: `${position.left}px`, top: `${position.top}px` }}
                onClick={(event) => event.stopPropagation()}
                onKeyDown={handleMenuKeyDown}
            >
                {!canPreview ? (
                    <p className={styles.actionsUnavailable}>Playable media is not ready yet.</p>
                ) : null}
                <button
                    type="button"
                    role="menuitem"
                    aria-disabled={!canPreview}
                    title={!canPreview ? 'Playable media is not ready yet.' : undefined}
                    onClick={() => {
                        if (!canPreview) return;
                        closeMenu();
                        onPreview();
                    }}
                >
                    Preview here
                </button>
                <Link
                    role="menuitem"
                    to={`${Routes.WATCH}?v=${encodeURIComponent(shortcode)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-disabled={!canPreview}
                    title={!canPreview ? 'Playable media is not ready yet.' : undefined}
                    onClick={(event) => {
                        if (!canPreview) {
                            event.preventDefault();
                            return;
                        }
                        closeMenu();
                    }}
                >
                    Open player page
                </Link>
            </div>,
            document.body
        )
        : null;

    return (
        <div className={styles.actionsMenu} onClick={(event) => event.stopPropagation()}>
            <button
                ref={triggerRef}
                type="button"
                className={styles.actionsTrigger}
                aria-label={`Video actions for ${title}`}
                aria-haspopup="menu"
                aria-expanded={isOpen}
                aria-controls={isOpen ? menuId : undefined}
                onClick={() => setIsOpen((current) => !current)}
            >
                Video actions
                <span aria-hidden="true" className={styles.actionsChevron}>⌄</span>
            </button>
            {menu}
        </div>
    );
};

interface AdminFloatingVideoPreviewProps {
    video: AdminVideoPreviewTarget | null;
    token?: string | null;
    onClose: () => void;
}

export const AdminFloatingVideoPreview = ({ video, token, onClose }: AdminFloatingVideoPreviewProps) => {
    const previewTitleId = useId();
    const shortcode = video?.shortcode ?? '';
    const [playbackUrl, setPlaybackUrl] = useState('');
    const [loadedShortcode, setLoadedShortcode] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [position, setPosition] = useState({ x: viewportMargin, y: viewportMargin });
    const [isDragging, setIsDragging] = useState(false);
    const panelRef = useRef<HTMLDivElement | null>(null);
    const dragOffsetRef = useRef<{ x: number; y: number } | null>(null);

    const clampPosition = useCallback((nextX: number, nextY: number) => {
        if (typeof window === 'undefined') return { x: nextX, y: nextY };
        const fallbackWidth = Math.min(previewMaxWidth, Math.max(0, window.innerWidth - (viewportMargin * 2)));
        const fallbackHeight = previewHeaderFallbackHeight + (fallbackWidth * 9 / 16);
        const panelWidth = panelRef.current?.offsetWidth || fallbackWidth;
        const panelHeight = panelRef.current?.offsetHeight || fallbackHeight;
        return clampPreviewPosition(
            nextX,
            nextY,
            window.innerWidth,
            window.innerHeight,
            panelWidth,
            panelHeight
        );
    }, []);

    useEffect(() => {
        setPlaybackUrl('');
        setLoadedShortcode('');
        setErrorMessage(null);
        setIsDragging(false);
        dragOffsetRef.current = null;
        if (!shortcode) {
            setIsLoading(false);
            return;
        }

        const fallbackWidth = Math.min(previewMaxWidth, Math.max(0, window.innerWidth - (viewportMargin * 2)));
        const fallbackHeight = previewHeaderFallbackHeight + (fallbackWidth * 9 / 16);
        setPosition({
            x: Math.max(viewportMargin, window.innerWidth - fallbackWidth - viewportMargin),
            y: Math.max(viewportMargin, window.innerHeight - fallbackHeight - viewportMargin)
        });
        let active = true;
        setIsLoading(true);
        fetchVideoPlaybackSource(shortcode, undefined, token)
            .then((source) => {
                if (active) {
                    setPlaybackUrl(source.Url);
                    setLoadedShortcode(shortcode);
                }
            })
            .catch(() => {
                if (active) setErrorMessage('Unable to prepare video preview.');
            })
            .finally(() => {
                if (active) setIsLoading(false);
            });
        return () => {
            active = false;
        };
    }, [token, shortcode]);

    useEffect(() => {
        if (!video) return undefined;
        const handleEscape = (event: globalThis.KeyboardEvent) => {
            if (event.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);
    }, [onClose, video]);

    useEffect(() => {
        if (!video) return undefined;
        const handleResize = () => setPosition((previous) => clampPosition(previous.x, previous.y));
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [clampPosition, video]);

    useEffect(() => {
        if (!video) return undefined;
        const frame = window.requestAnimationFrame(() => {
            setPosition((previous) => clampPosition(previous.x, previous.y));
        });
        return () => window.cancelAnimationFrame(frame);
    }, [clampPosition, errorMessage, isLoading, playbackUrl, video]);

    useEffect(() => {
        const panel = panelRef.current;
        if (!video || !panel || typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(() => {
            setPosition((previous) => clampPosition(previous.x, previous.y));
        });
        observer.observe(panel);
        return () => observer.disconnect();
    }, [clampPosition, video]);

    if (!video) return null;

    const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
        if ((event.button ?? 0) !== 0 || (event.target as HTMLElement).closest('button')) return;
        const bounds = panelRef.current?.getBoundingClientRect();
        if (!bounds) return;
        dragOffsetRef.current = { x: event.clientX - bounds.left, y: event.clientY - bounds.top };
        setIsDragging(true);
        event.currentTarget.setPointerCapture?.(event.pointerId);
    };

    const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
        if (!dragOffsetRef.current) return;
        setPosition(clampPosition(event.clientX - dragOffsetRef.current.x, event.clientY - dragOffsetRef.current.y));
    };

    const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
        if (event.currentTarget.hasPointerCapture?.(event.pointerId)) event.currentTarget.releasePointerCapture?.(event.pointerId);
        setIsDragging(false);
        dragOffsetRef.current = null;
    };

    const hasLoadedSource = playbackUrl && loadedShortcode === video.shortcode;

    return (
        <div
            role="dialog"
            aria-modal="false"
            aria-labelledby={previewTitleId}
            className={`${styles.floatingPreview} ${isDragging ? styles.dragging : ''}`}
            style={{ left: `${position.x}px`, top: `${position.y}px` }}
            ref={panelRef}
        >
            <div
                className={styles.previewHeader}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerUp}
            >
                <div className={styles.previewHeaderText}>
                    <h2 id={previewTitleId}>Video preview</h2>
                    <p>{video.title}</p>
                </div>
                <button type="button" className={styles.previewClose} aria-label="Close video preview" onClick={onClose}>
                    <X aria-hidden="true" size={18} />
                </button>
            </div>
            <div className={styles.previewSurface}>
                {hasLoadedSource ? (
                    <video
                        aria-label={`Preview ${video.title}`}
                        controls
                        controlsList="nodownload"
                        className={styles.previewPlayer}
                        src={playbackUrl}
                    >
                        Your browser does not support the video player.
                    </video>
                ) : errorMessage ? (
                    <p className={styles.previewMessage} role="alert">{errorMessage}</p>
                ) : isLoading ? (
                    <p className={styles.previewMessage} role="status">Preparing preview…</p>
                ) : null}
            </div>
        </div>
    );
};

interface AdminVideoPlayerToolsProps extends AdminVideoPreviewTarget {
    canPreview?: boolean;
    token?: string | null;
}

const AdminVideoPlayerTools = ({ title, shortcode, canPreview = true, token }: AdminVideoPlayerToolsProps) => {
    const target = { title, shortcode };
    const [preview, setPreview] = useState<AdminVideoPreviewTarget | null>(null);
    const closePreview = useCallback(() => setPreview(null), []);
    return (
        <>
            <AdminVideoActionsMenu {...target} canPreview={canPreview} onPreview={() => setPreview(target)} />
            <AdminFloatingVideoPreview video={preview} token={token} onClose={closePreview} />
        </>
    );
};

export default AdminVideoPlayerTools;
